import { Box, Grid, TextField } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Editable postal address block — the billing / shipping address fields shared
 * by the customer and site-group settings surfaces.
 *
 * Fully controlled: the parent owns the address value and feeds every edit
 * straight back in through `address`. Mirroring `address` into local state here
 * would close a render cycle with that parent, so this component holds no state
 * of its own and emits `onChange` only from real user input.
 *
 * @param {Object} address - The address row being edited. Fields may be null on
 *   rows loaded from the server; each binding coerces to '' so the inputs stay
 *   controlled.
 * @param {(next: Object) => void} [onChange] - Receives the full address row
 *   with the edited field applied.
 * @param {string} [variant] - MUI TextField variant.
 */
function AddressForm({ address, onChange, variant }) {
  const { t } = useTranslation();

  const handleChange = e => {
    const { name, value } = e.target;
    onChange?.({ ...address, [name]: value });
  };

  return (
    <Box component="form">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            size="small"
            fullWidth
            variant={variant}
            label={t('customer.addressLine1')}
            name="address_1"
            value={address.address_1 ?? ''}
            onChange={handleChange}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            size="small"
            fullWidth
            variant={variant}
            label={t('customer.addressLine2')}
            name="address_2"
            value={address.address_2 ?? ''}
            onChange={handleChange}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            size="small"
            fullWidth
            variant={variant}
            label={t('customer.city')}
            name="city"
            value={address.city ?? ''}
            onChange={handleChange}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            size="small"
            fullWidth
            variant={variant}
            label={t('customer.state')}
            name="state"
            value={address.state ?? ''}
            onChange={handleChange}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            size="small"
            fullWidth
            variant={variant}
            label={t('customer.zip')}
            name="zipcode"
            value={address.zipcode ?? ''}
            onChange={handleChange}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            size="small"
            fullWidth
            variant={variant}
            label={t('customer.country')}
            name="country"
            value={address.country ?? ''}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default AddressForm;
